import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { isAppRoot, isPanelPage } from "@/lib/utils"

type AlbumContextType = {
    albumId: string;
    albumSlug: string;
    loading: boolean;
    isActive: boolean;
    isReserved: boolean;
};

const AlbumContext = createContext<AlbumContextType>({
    albumId: '',
    albumSlug: '',
    loading: true,
    isActive: false,
    isReserved: false,
});

export const AlbumProvider = ({ children }: { children: React.ReactNode }) => {
    const [albumId, setAlbumId] = useState('');
    const [albumSlug, setAlbumSlug] = useState('');
    const [loading, setLoading] = useState(true);
    const [isActive, setIsActive] = useState(false);
    const [isReserved, setIsReserved] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const isRoot = isAppRoot(location)
    const onPanel = isPanelPage(location)
    const isPanel = isPanelPage(location)


    useEffect(() => {


        const resolveAlbum = async () => {


            const pathSegments = location.pathname.split('/').filter(Boolean);
            //  console.log("DEBUG 🟠: path segments ", pathSegments);
            // If we're on the root path or on /panel, don't try to resolve album
            if (pathSegments.length === 0 || isRoot || onPanel) {
                setAlbumId("");
                setAlbumSlug("");
                setIsActive(false);
                setIsReserved(false);
                setLoading(false);
                return;
            }

            const currentSlug = pathSegments[0];
            //    console.log('AlbumProvider: Current slug from path:', currentSlug);

            // If already resolved and slug matches, don't resolve again
            if (albumId && albumSlug === currentSlug) {
                // console.log('AlbumProvider: Already resolved, setting loading to false');
                setLoading(false);
                return;
            }

            // console.log('AlbumProvider: Fetching album data for slug:', currentSlug);
            try {
                const { data, error } = await supabase
                    .from('albums')
                    .select('id, slug, is_active, is_reserved')
                    .eq('slug', currentSlug)
                    .maybeSingle();

                if (error) {
                    console.error('[AlbumProvider] Error fetching album:', error);
                    toast({
                        title: "Error",
                        description: "Error al cargar el álbum",
                        variant: "destructive"
                    });
                    setAlbumId("");
                    setAlbumSlug("");
                    setIsActive(false);
                    setIsReserved(false);
                    navigate('/', { replace: true });
                    return;
                }

                if (!data || (!data.is_active && !data.is_reserved)) {
                    console.error('[AlbumProvider] Album not found for slug:', currentSlug);
                    toast({
                        title: "Error",
                        description: "Álbum no disponible",
                        variant: "destructive"
                    });
                    setAlbumId("");
                    setAlbumSlug("");
                    setIsActive(false);
                    setIsReserved(false);
                    navigate('/', { replace: true });
                    return;
                }

                setAlbumId((data as any).id);
                setAlbumSlug((data as any).slug);
                setIsActive(!!(data as any).is_active);
                setIsReserved(!!(data as any).is_reserved);
            } catch (error) {
                console.error('[AlbumProvider] Error resolving album:', error);
                setAlbumId("");
                setAlbumSlug("");
                setIsActive(false);
                setIsReserved(false);
                navigate('/', { replace: true });
            } finally {
                setLoading(false);
            }
        };

        // dashboard panel
        if (!isPanel) {
            resolveAlbum();
        }
    }, [location.pathname]); // Remove albumId and albumSlug from dependencies to prevent unnecessary re-runs

    return (
        <AlbumContext.Provider value={{ albumId, albumSlug, loading, isActive, isReserved }}>
            {children}
        </AlbumContext.Provider>
    );
};

export const useAlbumContext = () => useContext(AlbumContext);
